import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";

const SHELF_IMG = "/images/shelf-cabinet.jpg";
const BASKETS_IMG = "/images/book-baskets.jpg";

const shelves = [
  { name: "Picture Books", note: "First sparks for ages 2–6", tag: "bg-sage/25 text-ink" },
  { name: "Early Readers", note: "Brave first chapters, ages 6–8", tag: "bg-terracotta/20 text-ink" },
  { name: "Middle Grade", note: "Whole worlds for ages 8–12", tag: "bg-plum/10 text-plum" },
  { name: "Nature & Making", note: "Field guides, craft, science", tag: "bg-sand text-ink" },
];

export const Collection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      id="collection"
      ref={ref}
      data-testid="collection-section"
      className="mx-auto max-w-[1400px] px-6 py-28 md:px-12 md:py-40"
    >
      <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="mb-6 text-xs font-semibold uppercase tracking-[0.25em] text-plum">
              01 · The Shelves
            </p>
            <h2 className="font-serif text-4xl leading-tight tracking-tight text-ink sm:text-5xl md:text-6xl">
              Seven thousand doors, all slightly <em className="text-plum">ajar.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-md text-base leading-relaxed text-slate2 md:text-lg">
              From the very first picture book to the middle-grade novel a child
              can't put down, our collection is hand-chosen for wonder — not for
              worksheets. Warm wood, low shelves, and books faced outward like
              invitations.
            </p>
          </Reveal>

          <div className="mt-12 space-y-0">
            {shelves.map((s, i) => (
              <Reveal key={s.name} delay={0.1 + i * 0.08}>
                <div
                  data-testid={`collection-shelf-${i}`}
                  className="group flex items-center justify-between border-t border-ink/10 py-5 transition-colors duration-300 last:border-b hover:bg-sand/60"
                >
                  <div className="flex items-baseline gap-5 px-2">
                    <span className="font-serif text-sm italic text-plum">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <p className="font-serif text-2xl text-ink">{s.name}</p>
                      <p className="mt-0.5 text-sm text-slate2">{s.note}</p>
                    </div>
                  </div>
                  <span
                    className={`mr-2 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] ${s.tag}`}
                  >
                    Ages {["2–6", "6–8", "8–12", "All"][i]}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.3}>
            <a
              href="#visit"
              data-testid="collection-cta"
              className="group mt-12 inline-flex items-center gap-2 rounded-full bg-plum px-8 py-4 text-sm font-semibold text-cream transition-transform duration-300 hover:scale-105"
            >
              Explore Our Book Collection
              <ArrowUpRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </a>
          </Reveal>
        </div>

        <div className="relative lg:col-span-7">
          <Reveal delay={0.1} className="lg:sticky lg:top-32">
            <div className="relative overflow-hidden rounded-3xl">
              <motion.img
                src={SHELF_IMG}
                alt="The library's wooden book cabinet beneath the paper tree branch"
                data-testid="collection-image"
                style={{ y, scale: 1.12 }}
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
            <div
              data-testid="collection-caption"
              className="mt-6 flex items-start justify-between gap-6"
            >
              <p className="max-w-sm font-serif text-xl italic leading-snug text-ink/80">
                "We face the covers outward, because a book should call to a
                child before we ever do."
              </p>
              <p className="shrink-0 text-xs uppercase tracking-[0.2em] text-slate2">
                The reading room
              </p>
            </div>
            <div className="mt-8 grid grid-cols-1 items-center gap-6 sm:grid-cols-2">
              <div className="overflow-hidden rounded-2xl">
                <img
                  src={BASKETS_IMG}
                  alt="Series baskets on the library shelf, Malory Towers facing out"
                  data-testid="collection-baskets-image"
                  className="aspect-[4/3] w-full object-cover"
                />
              </div>
              <p className="font-serif text-lg italic leading-snug text-ink/70">
                "Baskets, not stacks — every series sits within a small child's
                reach, labels handwritten and slightly crooked, the way it
                should be."
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
